import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, Table, Space, Button, Input, Select, Form, Row, Col, message, Typography, Tag, Pagination, Switch, Modal, Checkbox, Divider, Image, Tooltip, Descriptions } from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  FilterOutlined,
  ReloadOutlined,
  DownloadOutlined,
  CopyOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { TableProps } from 'antd';
import debounce from 'lodash/debounce';
import { getProductList, getAllProducts, deleteProduct } from '../../api/product';
import request from '../../utils/request';
import { getCurrentUserProjects, getCustomerList } from '../../services/project';
import type { Product } from '../../types/product';
import type { Project } from '../../types/project';
import { useTableQuery } from '../../hooks/useTableQuery';
import { useTabStateManagement } from '../../hooks/useTabStateManagement';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../contexts/PermissionContext';

const { Option } = Select;
const { Title } = Typography;

interface ProductFilters {
  name?: string;
  custom_code?: string;
  system_code?: string;
  sorting_codes?: string;
  has_logistics_package?: boolean | "unlimited";
  creation_source?: string;
  projects?: string;
  customer?: string; // 新增：客户筛选字段
}

/** 与后端 product_sorting_code_payloads 一致：条码编码 + 本产品下的说明（ProductSortingCode.description） */
const SORTING_CODES_CELL_MAX = 3;

type SortingCodeRow = NonNullable<Product['sorting_codes']>[number];

const SortingCodesTableCell: React.FC<{ sorting_codes?: SortingCodeRow[] | null }> = ({ sorting_codes }) => {
  const [modalOpen, setModalOpen] = useState(false);

  if (!sorting_codes?.length) {
    return <span style={{ color: '#999' }}>-</span>;
  }

  const list = sorting_codes;
  const visible = list.slice(0, SORTING_CODES_CELL_MAX);
  const hasMore = list.length > SORTING_CODES_CELL_MAX;

  const descTooltipTitle = (description?: string) => {
    const t = String(description ?? '').trim();
    if (!t) return '暂无说明';
    return (
      <div style={{ maxWidth: 360, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {t}
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          position: 'relative',
          minHeight: hasMore ? SORTING_CODES_CELL_MAX * 22 + 4 : undefined,
          paddingRight: hasMore ? 26 : undefined,
        }}
      >
        <div>
          {visible.map((item) => (
            <div
              key={item.id}
              style={{
                lineHeight: '22px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Tooltip
                title={descTooltipTitle(item.description)}
                placement="topLeft"
                overlayStyle={{ maxWidth: 400 }}
              >
                <span style={{ cursor: 'default' }}>{item.code}</span>
              </Tooltip>
            </div>
          ))}
        </div>
        {hasMore && (
          <Button
            type="link"
            size="small"
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              padding: 0,
              height: 22,
              lineHeight: '22px',
              fontSize: 16,
              fontWeight: 600,
            }}
            aria-label="查看全部分拣条码"
            onClick={(e) => {
              e.stopPropagation();
              setModalOpen(true);
            }}
          >
            ⋯
          </Button>
        )}
      </div>
      <Modal
        title="分拣条码（全部）"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={
          <Button type="primary" onClick={() => setModalOpen(false)}>
            关闭
          </Button>
        }
        width={520}
        destroyOnClose
      >
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {list.map((item) => (
            <div
              key={item.id}
              style={{
                marginBottom: 12,
                paddingBottom: 12,
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <div style={{ fontWeight: 600 }}>{item.code}</div>
              <div style={{ color: '#666', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {String(item.description ?? '').trim() || '暂无说明'}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

const PROJECTS_CELL_MAX = 3;

/** 列表接口中 projects 多为对象数组（与 ProjectSerializer 一致） */
type ProjectListRow = {
  id?: number;
  project_code?: string;
  code?: string;
  name?: string;
};

const ProjectsTableCell: React.FC<{ projects?: ProjectListRow[] | null }> = ({ projects }) => {
  const [modalOpen, setModalOpen] = useState(false);

  if (!projects?.length) {
    return <span style={{ color: '#999' }}>-</span>;
  }

  const list = projects;
  const visible = list.slice(0, PROJECTS_CELL_MAX);
  const hasMore = list.length > PROJECTS_CELL_MAX;

  const rowKey = (p: ProjectListRow, index: number) =>
    String(p.id ?? p.project_code ?? p.code ?? `idx-${index}`);

  const tooltipTitle = (p: ProjectListRow) => {
    const code = String(p.project_code ?? p.code ?? p.id ?? '-');
    const name = p.name ? String(p.name).trim() : '';
    return (
      <div style={{ maxWidth: 360 }}>
        <div>
          <strong>项目编码：</strong>
          {code}
        </div>
        {name ? (
          <div style={{ marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            <strong>项目名称：</strong>
            {name}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          position: 'relative',
          minHeight: hasMore ? PROJECTS_CELL_MAX * 22 + 4 : undefined,
          paddingRight: hasMore ? 26 : undefined,
        }}
      >
        <div>
          {visible.map((p, index) => (
            <div
              key={rowKey(p, index)}
              style={{
                lineHeight: '22px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Tooltip title={tooltipTitle(p)} placement="topLeft" overlayStyle={{ maxWidth: 400 }}>
                <span style={{ cursor: 'default' }}>
                  <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                    {String(p.project_code ?? p.code ?? '-')}
                  </span>
                  {p.name ? (
                    <span style={{ color: '#666', marginLeft: 8 }}>{String(p.name)}</span>
                  ) : null}
                </span>
              </Tooltip>
            </div>
          ))}
        </div>
        {hasMore && (
          <Button
            type="link"
            size="small"
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              padding: 0,
              height: 22,
              lineHeight: '22px',
              fontSize: 16,
              fontWeight: 600,
            }}
            aria-label="查看全部已关联项目"
            onClick={(e) => {
              e.stopPropagation();
              setModalOpen(true);
            }}
          >
            ⋯
          </Button>
        )}
      </div>
      <Modal
        title="已关联项目（全部）"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={
          <Button type="primary" onClick={() => setModalOpen(false)}>
            关闭
          </Button>
        }
        width={520}
        destroyOnClose
      >
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {list.map((p, index) => (
            <div
              key={rowKey(p, index)}
              style={{
                marginBottom: 12,
                paddingBottom: 12,
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <div>
                <span style={{ fontWeight: 600, color: '#1890ff' }}>
                  {String(p.project_code ?? p.code ?? p.id ?? '-')}
                </span>
                {p.name ? (
                  <span style={{ color: '#666', marginLeft: 8 }}>{String(p.name)}</span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

/** 与表格「物流包装」列一致的彩色标签 */
function renderLogisticsPackageBadge(value: boolean | undefined) {
  if (value === true) {
    return (
      <span
        style={{
          backgroundColor: '#dcfce7',
          color: '#166534',
          padding: '2px 8px',
          borderRadius: 6,
          fontSize: 12,
        }}
      >
        自带
      </span>
    );
  }
  if (value === false) {
    return (
      <span
        style={{
          backgroundColor: '#fee2e2',
          color: '#b91c1c',
          padding: '2px 8px',
          borderRadius: 6,
          fontSize: 12,
        }}
      >
        无
      </span>
    );
  }
  return <span style={{ color: '#999' }}>-</span>;
}

function formatLogisticsPackagePlainText(value: boolean | undefined): string {
  if (value === true) return '自带';
  if (value === false) return '无';
  return '-';
}

/** 与弹窗展示一致的可读文本，不含产品图片（用于复制） */
function buildProductFullInfoPlainText(product: Product, hideCustomer: boolean): string {
  const r = product as unknown as Record<string, unknown>;
  const sourceMap: Record<string, string> = {
    customer: '客户或仓库代创建',
    warehouse: '仓库创建',
    other: '其他',
  };
  const formatDT = (s?: string) => {
    if (!s) return '-';
    try {
      return new Date(s).toLocaleString('zh-CN');
    } catch {
      return s;
    }
  };
  const createdByLabel = () => {
    const cb = r.created_by as { username?: string; role?: string } | number | null | undefined;
    if (cb == null) return '-';
    if (typeof cb === 'object' && cb && 'username' in cb && cb.username) {
      return `${cb.username}${cb.role ? ` (${cb.role})` : ''}`;
    }
    return String(cb);
  };

  const projects = product.projects as unknown;
  const projectList = Array.isArray(projects) ? (projects as Array<Record<string, unknown>>) : [];

  const blocks: string[] = [];

  blocks.push(
    [
      '【基本信息】',
      `ID：${product.id}`,
      `产品名称：${product.name || '-'}`,
      `系统编码：${product.system_code || '-'}`,
      `客户自定义编码：${product.custom_code || '-'}`,
      `产品描述：${product.description?.trim() || '-'}`,
    ].join('\n'),
  );

  let sortingBlock = '【分拣条码】\n';
  if (Array.isArray(product.sorting_codes) && product.sorting_codes.length > 0) {
    sortingBlock += product.sorting_codes
      .map((sc, i) => {
        const desc = String(sc.description ?? '').trim();
        return `${i + 1}. ${sc.code}${desc ? ` — ${desc}` : ' — （无说明）'}`;
      })
      .join('\n');
  } else {
    sortingBlock += '-';
  }
  blocks.push(sortingBlock);

  let projectBlock = '【已关联项目】\n';
  if (projectList.length > 0) {
    projectBlock += projectList
      .map((proj, i) => {
        const code = String(proj.project_code ?? proj.code ?? proj.id ?? '-');
        const name = proj.name ? String(proj.name) : '';
        return `${i + 1}. ${code}${name ? ` ${name}` : ''}`;
      })
      .join('\n');
  } else {
    projectBlock += '-';
  }
  blocks.push(projectBlock);

  blocks.push(
    [
      '【产品属性】',
      `声明价值(USD)：${product.value_usd != null ? Number(product.value_usd).toFixed(2) : '-'}`,
      `重量(g)：${product.weight_grams ?? '-'}`,
      `物流包装：${formatLogisticsPackagePlainText(product.has_logistics_package)}`,
      `长度(cm)：${product.length_cm ?? '-'}`,
      `宽度(cm)：${product.width_cm ?? '-'}`,
      `高度(cm)：${product.height_cm ?? '-'}`,
    ].join('\n'),
  );

  const createLines: string[] = ['【创建与归属】'];
  if (!hideCustomer) {
    createLines.push(
      `所属客户：${
        product.customer && typeof product.customer === 'object' && product.customer.username
          ? product.customer.username
          : '-'
      }`,
    );
  }
  createLines.push(
    `创建来源：${sourceMap[product.creation_source || ''] || product.creation_source || '-'}`,
    `创建者：${createdByLabel()}`,
    `创建时间：${formatDT(product.created_at)}`,
  );
  blocks.push(createLines.join('\n'));

  return blocks.join('\n\n');
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

/** 产品列表「完整信息」弹窗正文（数据与列表行一致，来自同一套 ProductSerializer） */
const ProductFullInfoModalBody: React.FC<{ product: Product; hideCustomer: boolean }> = ({
  product,
  hideCustomer,
}) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const r = product as unknown as Record<string, unknown>;
  const photoUrl =
    (r.photo as string) ||
    (r.photo_url as string) ||
    (r.image_url as string) ||
    (r.image as string);
  const normalizedDescription = String(product.description || '').trim();
  const shouldTruncateDescription = normalizedDescription.length > 100;
  const descriptionText = shouldTruncateDescription && !descriptionExpanded
    ? `${normalizedDescription.slice(0, 100)}...`
    : normalizedDescription;

  useEffect(() => {
    setDescriptionExpanded(false);
  }, [product.id]);

  const formatDT = (s?: string) => {
    if (!s) return '-';
    try {
      return new Date(s).toLocaleString('zh-CN');
    } catch {
      return s;
    }
  };

  const createdByLabel = () => {
    const cb = r.created_by as { username?: string; role?: string } | number | null | undefined;
    if (cb == null) return '-';
    if (typeof cb === 'object' && cb && 'username' in cb && cb.username) {
      return `${cb.username}${cb.role ? ` (${cb.role})` : ''}`;
    }
    return String(cb);
  };

  const projects = product.projects as unknown;
  const projectList = Array.isArray(projects) ? (projects as Array<Record<string, unknown>>) : [];

  return (
    <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
      <Descriptions
        title="基本信息"
        bordered
        size="small"
        column={2}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label="ID">{product.id}</Descriptions.Item>
        <Descriptions.Item label="产品名称">{product.name || '-'}</Descriptions.Item>
        <Descriptions.Item label="客户自定义编码">{product.custom_code || '-'}</Descriptions.Item>
        <Descriptions.Item label="系统编码">{product.system_code || '-'}</Descriptions.Item>
        <Descriptions.Item label="产品描述" span={2}>
          {normalizedDescription ? (
            <div>
              <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {descriptionText}
              </span>
              {shouldTruncateDescription ? (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, marginLeft: 8, height: 'auto' }}
                  onClick={() => setDescriptionExpanded((prev) => !prev)}
                >
                  {descriptionExpanded ? '收起' : '展开'}
                </Button>
              ) : null}
            </div>
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions title="分拣条码" bordered size="small" column={1} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="列表">
          {Array.isArray(product.sorting_codes) && product.sorting_codes.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {product.sorting_codes.map((sc) => (
                <li key={sc.id} style={{ marginBottom: 8 }}>
                  <strong>{sc.code}</strong>
                  {String(sc.description ?? '').trim() ? (
                    <div style={{ color: '#666', fontSize: 13, marginTop: 2, whiteSpace: 'pre-wrap' }}>
                      {String(sc.description).trim()}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontSize: 13 }}>（无说明）</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions title="已关联项目" bordered size="small" column={1} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="项目">
          {projectList.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {projectList.map((proj) => {
                const id = (proj.id as number | string) ?? proj.project_code ?? proj.code;
                return (
                  <li key={String(id)}>
                    <span style={{ fontWeight: 600, color: '#1890ff' }}>
                      {String(proj.project_code ?? proj.code ?? proj.id ?? '-')}
                    </span>
                    {proj.name ? (
                      <span style={{ marginLeft: 8, color: '#666' }}>{String(proj.name)}</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            '-'
          )}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        title="产品属性"
        bordered
        size="small"
        column={{ xs: 1, sm: 3 }}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label="声明价值(USD)">
          {product.value_usd != null ? Number(product.value_usd).toFixed(2) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="重量(g)">{product.weight_grams ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="物流包装">
          {renderLogisticsPackageBadge(product.has_logistics_package)}
        </Descriptions.Item>
        <Descriptions.Item label="长度(cm)">{product.length_cm ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="宽度(cm)">{product.width_cm ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="高度(cm)">{product.height_cm ?? '-'}</Descriptions.Item>
      </Descriptions>

      <Descriptions title="创建与归属" bordered size="small" column={{ xs: 1, sm: 2 }}>
        {!hideCustomer ? (
          <Descriptions.Item label="所属客户">
            {product.customer && typeof product.customer === 'object' && product.customer.username
              ? product.customer.username
              : '-'}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label="创建者">{createdByLabel()}</Descriptions.Item>
        <Descriptions.Item label="创建时间">{formatDT(product.created_at)}</Descriptions.Item>
      </Descriptions>

      {photoUrl ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>产品图片</div>
          <Image src={photoUrl} alt={product.name} style={{ maxWidth: 280, borderRadius: 8 }} />
        </div>
      ) : null}
    </div>
  );
};

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, isMainAccount, hiddenFields, exportHiddenFields } = usePermissions();
  /** 主账号默认可创建；子账号需具备 product:create 权限 */
  const canCreateProduct = hasPermission('product:create') || isMainAccount();
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [customers, setCustomers] = useState<Array<{ id: string; username: string }>>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  // 基于敏感字段策略判断是否隐藏“所属客户”
  const _hiddenFields = hiddenFields('product.Product');
  const hideCustomerByStrategy = Array.isArray(_hiddenFields) && _hiddenFields.includes('customer__username');
  // UPS 诊断信息
  const [upsInfo, setUpsInfo] = useState<any>(null);
  const [upsLoading, setUpsLoading] = useState<boolean>(false);
  const [upsError, setUpsError] = useState<string | null>(null);
  
  // 批量下载相关状态
  const [downloading, setDownloading] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [selectedDownloadFields, setSelectedDownloadFields] = useState<string[]>([]);
  const [productFullInfoRecord, setProductFullInfoRecord] = useState<Product | null>(null);

  // 批量下载字段配置
  const DOWNLOAD_FIELDS = [
    {
      group: '基本信息',
      fields: [
        { key: 'customer', label: '所属客户' },
        { key: 'name', label: '产品名称' },
        { key: 'custom_code', label: '客户自定义编码' },
        { key: 'system_code', label: '系统编码' },
        { key: 'description', label: '产品描述' },
        { key: 'created_at', label: '创建时间' },
        { key: 'creation_source', label: '创建来源' },
      ]
    },
    {
      group: '分拣信息',
      fields: [
        { key: 'sorting_codes', label: '分拣条码' },
        { key: 'projects', label: '已关联项目' },
      ]
    },
    {
      group: '物理属性',
      fields: [
        { key: 'value_usd', label: '价值(USD)' },
        { key: 'weight_grams', label: '重量(g)' },
        { key: 'length_cm', label: '长度(cm)' },
        { key: 'width_cm', label: '宽度(cm)' },
        { key: 'height_cm', label: '高度(cm)' },
        { key: 'unit_volume', label: '单位体积(cm³)' },
        { key: 'has_logistics_package', label: '物流包装' },
      ]
    }
  ];

  // 导出敏感策略：计算产品导出时不可见字段，并将其映射到前端字段键
  const exportHidden = exportHiddenFields('product.Product');
  const isFieldHiddenForExport = useCallback((fieldKey: string) => {
    // 将前端字段键映射到后端敏感字段键
    const mapping: Record<string, string[]> = {
      // 所属客户（前端字段 customer）对应后端敏感键 customer__username
      customer: ['customer__username'],
    };
    const mapped = mapping[fieldKey] || [];
    if (!Array.isArray(exportHidden) || exportHidden.length === 0) return false;
    return mapped.some(k => exportHidden.includes(k));
  }, [exportHidden]);

  // 基于导出策略过滤可选导出字段（用于下载弹窗）
  const DOWNLOAD_FIELDS_ALLOWED = useMemo(() => (
    DOWNLOAD_FIELDS
      .map(group => ({
        ...group,
        fields: group.fields.filter(f => !isFieldHiddenForExport(f.key))
      }))
      .filter(group => group.fields.length > 0)
  ), [DOWNLOAD_FIELDS, isFieldHiddenForExport]);

  // 字段选择相关函数
  const handleFieldChange = (fieldKey: string, checked: boolean) => {
    if (checked) {
      setSelectedDownloadFields(prev => [...prev, fieldKey]);
    } else {
      setSelectedDownloadFields(prev => prev.filter(key => key !== fieldKey));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const currentFields = getFieldsByRole(user?.role || 'customer');
      const allFields = currentFields.flatMap(group => 
        group.fields.map(field => field.key)
      );
      setSelectedDownloadFields(allFields);
    } else {
      setSelectedDownloadFields([]);
    }
  };

  const handleGroupSelect = (groupFields: string[], checked: boolean) => {
    if (checked) {
      // 添加分组字段（去重）
      setSelectedDownloadFields(prev => {
        const combined = [...prev, ...groupFields];
        return combined.filter((item, index) => combined.indexOf(item) === index);
      });
    } else {
      // 移除分组字段
      setSelectedDownloadFields(prev => prev.filter(key => !groupFields.includes(key)));
    }
  };

  // 根据用户角色过滤字段
  const getFieldsByRole = (userRole: string) => {
    return DOWNLOAD_FIELDS_ALLOWED.map(group => ({
      ...group,
      fields: group.fields.filter(field => {
        // 客户后台不显示"所属客户"字段
        if (userRole === 'customer' && field.key === 'customer') {
          return false;
        }
        return true;
      })
    })).filter(group => group.fields.length > 0); // 过滤掉没有字段的分组
  };

  // 获取所有字段数量
  const getAllFieldsCount = () => {
    const currentFields = getFieldsByRole(user?.role || 'customer');
    return currentFields.flatMap(group => group.fields).length;
  };

  // 获取分组字段数量
  const getGroupFieldsCount = (group: any) => {
    return group.fields.length;
  };

  // 检查分组是否全选
  const checkGroupSelected = (group: any) => {
    const groupFields = group.fields.map((field: any) => field.key);
    return groupFields.every(field => selectedDownloadFields.includes(field));
  };

  // 检查分组是否部分选择
  const checkGroupIndeterminate = (group: any) => {
    const groupFields = group.fields.map((field: any) => field.key);
    const selectedCount = groupFields.filter(field => selectedDownloadFields.includes(field)).length;
    return selectedCount > 0 && selectedCount < groupFields.length;
  };
  
  // 使用ref来跟踪状态变化，避免无限循环
  const prevStateRef = useRef({
    expandedRows: [] as string[]
  });

  // 检查认证状态
  useEffect(() => {
    if (!user) {
      console.log('⚠️ 用户未登录，跳转到登录页');
      navigate('/login');
      return;
    }
    console.log('✅ 用户已登录:', user);
  }, [user, navigate]);

  // 获取项目列表
  useEffect(() => {
    const fetchProjects = async () => {
      if (user) {
        setProjectsLoading(true);
        try {
          const projectsData = await getCurrentUserProjects();
          setProjects(projectsData);
          console.log('✅ 获取项目列表成功:', projectsData);
        } catch (error) {
          console.error('❌ 获取项目列表失败:', error);
        } finally {
          setProjectsLoading(false);
        }
      }
    };
    
    fetchProjects();
  }, [user]);

  // 获取客户列表（仅对员工和管理员显示，且未被敏感策略隐藏）
  useEffect(() => {
    const fetchCustomers = async () => {
      // 只有员工和管理员需要看到客户筛选，且未被敏感策略隐藏
      if (user && (user.role === 'admin' || user.role === 'staff') && !hideCustomerByStrategy) {
        setCustomersLoading(true);
        try {
          const customersData = await getCustomerList({ onlyMain: true });
          setCustomers(customersData);
          console.log('✅ 获取客户列表成功:', customersData);
        } catch (error) {
          console.error('❌ 获取客户列表失败:', error);
        } finally {
          setCustomersLoading(false);
        }
      } else {
        setCustomers([]);
        setCustomersLoading(false);
      }
    };
    
    fetchCustomers();
  }, [user, hideCustomerByStrategy]);

  // 当策略隐藏客户信息时，清理筛选中的客户参数，避免无效参数外传
  // 注意：依赖于 state，需要在 useTableQuery 定义之后再声明

  // 获取 UPS 诊断信息（开发调试）
  const fetchUpsDiagnose = useCallback(async () => {
    if (!user) return;
    try {
      setUpsLoading(true);
      setUpsError(null);
      const resp = await request.get('/api/v1/permission-management/resources/ups-diagnose/', {
        params: { resource: 'product', action: 'read' }
      });
      setUpsInfo(resp.data);
    } catch (e: any) {
      console.error('❌ 获取 UPS 诊断信息失败:', e);
      setUpsError(e?.response?.data?.error || e?.message || '获取 UPS 诊断信息失败');
    } finally {
      setUpsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      fetchUpsDiagnose();
    }
  }, [fetchUpsDiagnose]);

  // 防抖的状态保存函数
  const debouncedSaveState = useCallback(
    debounce((state: any) => {
      saveState(state);
    }, 500),
    []
  );

  // 1. 使用useTableQuery管理分页、筛选、排序状态（URL同步）
  const { state, data, loading, actions, paginationConfig, error } = useTableQuery<ProductFilters>({
    initialState: {
      pagination: { current: 1, pageSize: 20, total: 0 },
      filters: { 
        name: '', 
        custom_code: '', 
        system_code: '',
        sorting_codes: '',
        has_logistics_package: undefined,
        creation_source: undefined,
        projects: undefined,
        customer: undefined // 新增：客户筛选初始值
      },
      sorting: { field: 'created_at', order: 'desc' }
    },
    urlConfig: {
      syncToUrl: true,
      urlKeys: ['pagination', 'filters', 'sorting']
    },
    fetchData: async (state) => {
      console.log('🚀 开始获取产品列表:', state);
      console.log('👤 当前用户:', user);
      
      // 构建API请求参数
      const apiParams: any = {
        page: state.pagination.current,
        page_size: state.pagination.pageSize,
      };
      
      // 添加筛选条件
      if (state.filters.name) {
        apiParams.search = state.filters.name;
      }
      if (state.filters.custom_code) {
        // 客户自定义编码使用专门的参数，不添加到search中
        apiParams.custom_code = state.filters.custom_code;
      }
      if (state.filters.system_code) {
        // 系统编码使用专门的参数，不添加到search中
        apiParams.system_code = state.filters.system_code;
      }
      if (state.filters.sorting_codes) {
        // 分拣条码通过专门的参数传递
        apiParams.sorting_codes = state.filters.sorting_codes;
      }
      if (state.filters.has_logistics_package !== undefined && state.filters.has_logistics_package !== "unlimited") {
        apiParams.has_logistics_package = state.filters.has_logistics_package;
      }
      if (state.filters.creation_source) {
        apiParams.creation_source = state.filters.creation_source;
      }
      if (state.filters.projects) {
        apiParams.projects = state.filters.projects;
      }
      if (!hideCustomerByStrategy && state.filters.customer) {
        // 客户筛选使用专门的参数传递
        apiParams.customer = state.filters.customer;
      }
      
      // 添加排序
      if (state.sorting.field && state.sorting.order) {
        const orderPrefix = state.sorting.order === 'desc' ? '-' : '';
        apiParams.ordering = `${orderPrefix}${state.sorting.field}`;
      }
      
      console.log('📡 API请求参数:', apiParams);
      
      try {
        const response = await getProductList(apiParams);
        console.log('✅ API响应:', response);
        
        if (!response || !Array.isArray(response.results)) {
          console.error('❌ API响应格式错误:', response);
          throw new Error('API 响应数据格式不正确');
        }
        
        // 调试：检查第一个产品的creation_source字段
        if (response.results.length > 0) {
          const firstProduct = response.results[0];
          console.log('🔍 第一个产品的creation_source字段:', {
            productId: firstProduct.id,
            productName: firstProduct.name,
            creation_source: firstProduct.creation_source,
            hasCreationSource: 'creation_source' in firstProduct,
            creationSourceType: typeof firstProduct.creation_source,
            allFields: Object.keys(firstProduct)
          });
        }
        
        console.log(`📊 获取到 ${response.results.length} 条产品数据，总数: ${response.count}`);
        
        return {
          results: response.results,
          count: response.count || 0
        };
      } catch (error) {
        console.error('❌ 获取产品列表失败:', error);
        throw error;
      }
    }
  });

  // 编辑/删除产品：按当前角色拼路由前缀
  const productBasePath =
    user?.role === 'admin' ? '/dashboard/admin' : user?.role === 'staff' ? '/dashboard/staff' : '/dashboard/customer';

  // 删除产品：仅未被任何库存单元使用的产品可删除（后端再次校验）
  const handleDeleteProduct = (record: Product) => {
    Modal.confirm({
      title: '确认删除产品',
      content: `确定删除产品「${record.name}」（${record.custom_code}）吗？删除后无法恢复。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteProduct(record.id);
          message.success('产品已删除');
          actions.refresh();
        } catch (error: any) {
          message.error(error?.response?.data?.detail || '删除失败，请稍后重试');
          throw error;
        }
      },
    });
  };

  // 当策略隐藏客户信息时，清理筛选中的客户参数，避免无效参数外传
  useEffect(() => {
    if (hideCustomerByStrategy && state.filters?.customer) {
      actions.updateFilters({ customer: undefined });
    }
  }, [hideCustomerByStrategy, state.filters?.customer, actions]);

  // 2. 使用useTabStateManagement管理页面特定状态（sessionStorage）
  const { saveState, restoreState, clearState } = useTabStateManagement({
    keepState: true,
    persistToStorage: true,
    customStorageKey: 'product_list_state'
  });

  // 3. 状态保存逻辑 - 修复无限循环
  useEffect(() => {
    // 检查状态是否真正发生了变化
    const hasExpandedRowsChanged = JSON.stringify(expandedRows) !== JSON.stringify(prevStateRef.current.expandedRows);
    
    if (hasExpandedRowsChanged) {
      // 更新ref中的状态
      prevStateRef.current = {
        expandedRows: [...expandedRows]
      };
      
      // 使用防抖保存状态
      debouncedSaveState({
        pageState: {
          scrollPosition: window.scrollY,
          expandedRows: expandedRows
        }
      });
    }
  }, [expandedRows, debouncedSaveState]);

  // 4. 页面卸载时保存状态 - 修复无限循环
  useEffect(() => {
    return () => {
      // 取消防抖的保存操作
      debouncedSaveState.cancel();
      
      // 立即保存最终状态
      saveState({
        pageState: {
          scrollPosition: window.scrollY,
          expandedRows: expandedRows
        }
      });
    };
  }, []); // 空依赖数组，只在卸载时执行

  // 5. 页面加载时恢复状态 - 修复无限循环
  useEffect(() => {
    // 只在组件挂载时恢复一次状态
    const savedState = restoreState();
    if (savedState?.pageState) {
      console.log('🔄 恢复页面状态:', savedState.pageState);
      
      // 恢复展开行状态
      if (savedState.pageState.expandedRows && savedState.pageState.expandedRows.length > 0) {
        setExpandedRows(savedState.pageState.expandedRows);
        prevStateRef.current.expandedRows = [...savedState.pageState.expandedRows];
      }
      
      // 恢复滚动位置
      if (savedState.pageState.scrollPosition && savedState.pageState.scrollPosition > 0) {
        // 延迟执行滚动，确保DOM已渲染
        setTimeout(() => {
          window.scrollTo(0, savedState.pageState.scrollPosition);
        }, 100);
      }
    }
  }, []); // 空依赖数组，只在挂载时执行

  // 6. 处理表格变化（分页、排序、筛选）
  const handleTableChange: TableProps<Product>['onChange'] = (pagination, filters, sorter) => {
    console.log('📊 表格变化:', { pagination, filters, sorter });
    
    // 更新分页状态
    if (pagination.current !== state.pagination.current || 
        pagination.pageSize !== state.pagination.pageSize) {
      actions.updatePagination({
        current: pagination.current || 1,
        pageSize: pagination.pageSize || 20
      });
    }
    
    // 更新排序状态 - 处理sorter可能是数组的情况
    if (sorter && !Array.isArray(sorter)) {
      const sorterField = sorter.field as string;
      const sorterOrder = sorter.order === 'descend' ? 'desc' : 
                          sorter.order === 'ascend' ? 'asc' : undefined;
      
      if (sorterField && sorterOrder && 
          (sorterField !== state.sorting.field || sorterOrder !== state.sorting.order)) {
        actions.updateSorting({
          field: sorterField,
          order: sorterOrder
        });
      }
    }
  };

  // 7. 处理搜索
  const handleSearch = debounce((value: string) => {
    console.log('🔍 搜索:', value);
    actions.updateFilters({ name: value });
  }, 500);

  // 8. 处理筛选
  const handleFilterChange = (key: keyof ProductFilters, value: any) => {
    console.log('🔍 筛选变化:', { key, value });
    actions.updateFilters({ [key]: value });
  };

  // 9. 重置筛选
  const handleReset = () => {
    console.log('🔄 重置筛选条件');
    actions.reset();
  };

  // 批量下载相关函数
  const handleShowDownloadModal = () => {
    if (state.pagination.total === 0) {
      message.warning('当前没有可下载的数据');
      return;
    }
    
    // 默认选择基于用户角色的所有字段
    const currentFields = getFieldsByRole(user?.role || 'customer');
    const allFields = currentFields.flatMap(group => 
      group.fields.map(field => field.key)
    );
    setSelectedDownloadFields(allFields);
    
    setDownloadModalVisible(true);
  };

  const handleConfirmDownload = async (selectedFields: string[]) => {
    setDownloadModalVisible(false);
    setDownloading(true);
    
    try {
      console.log('🔍 开始批量下载，筛选条件:', state.filters);
      console.log('🔍 选择的字段(原始):', selectedFields);
      // 基于导出策略过滤最终可导出的字段
      const allowedFieldSet = new Set(
        getFieldsByRole(user?.role || 'customer')
          .flatMap(group => group.fields.map(f => f.key))
      );
      const finalSelected = selectedFields.filter(k => allowedFieldSet.has(k));
      if (finalSelected.length === 0) {
        message.warning('无可导出的字段，请联系管理员调整敏感字段策略');
        return;
      }
      console.log('🔍 选择的字段(策略过滤后):', finalSelected);
      
      // 准备下载参数
      const downloadParams = {
        filters: state.filters,
        selected_fields: finalSelected,
        file_format: 'excel'
      };

      console.log('🔍 下载参数:', downloadParams);
      
      // 调用下载API
      await downloadProducts(downloadParams);
      
      message.success('产品数据下载成功！');
      
    } catch (error: any) {
      console.error('下载失败:', error);
      message.error(`下载失败: ${error.message || '未知错误'}`);
    } finally {
      setDownloading(false);
    }
  };

  const downloadProducts = async (params: any) => {
    try {
      console.log('📥 开始下载产品数据:', params);
      console.log('📥 当前筛选条件:', state.filters);
      
      // 构建API筛选参数
      const apiFilters: any = {};
      
      // 添加筛选条件
      if (state.filters.name) {
        apiFilters.search = state.filters.name;
      }
      if (state.filters.custom_code) {
        apiFilters.custom_code = state.filters.custom_code;
      }
      if (state.filters.system_code) {
        apiFilters.system_code = state.filters.system_code;
      }
      if (state.filters.sorting_codes) {
        apiFilters.sorting_codes = state.filters.sorting_codes;
      }
      if (state.filters.has_logistics_package !== undefined && state.filters.has_logistics_package !== "unlimited") {
        apiFilters.has_logistics_package = state.filters.has_logistics_package;
      }
      if (state.filters.creation_source) {
        apiFilters.creation_source = state.filters.creation_source;
      }
      if (state.filters.projects) {
        apiFilters.projects = state.filters.projects;
      }
      if (state.filters.customer) {
        apiFilters.customer = state.filters.customer;
      }
      
      // 添加排序
      if (state.sorting.field && state.sorting.order) {
        const orderPrefix = state.sorting.order === 'desc' ? '-' : '';
        apiFilters.ordering = `${orderPrefix}${state.sorting.field}`;
      }
      
      console.log('📥 API筛选参数:', apiFilters);
      
      // 获取所有符合筛选条件的产品数据
      console.log('📥 开始获取所有产品数据...');
      const allProducts = await getAllProducts(apiFilters);
      
      if (!allProducts || allProducts.length === 0) {
        throw new Error('没有符合筛选条件的产品数据可下载');
      }
      
      console.log(`📥 获取到所有产品数据，共${allProducts.length}条`);
      
      // 模拟网络延迟
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // 生成文件名（仅日期 + 筛选条件信息，不含具体时刻）
      const d = new Date();
      const timestamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const filterInfo = Object.keys(apiFilters).length > 0 ? '_筛选结果' : '_全部数据';
      const filename = `产品列表${filterInfo}_${timestamp}.csv`;
      
      console.log('📁 生成文件名:', filename);
      
      // 生成CSV文件内容
      console.log('🔍 开始生成CSV内容...');
      const csvContent = generateCSVContent(params, allProducts);
      
      if (!csvContent || csvContent === '暂无数据') {
        throw new Error('CSV内容生成失败');
      }
      
      console.log('✅ CSV内容生成成功，长度:', csvContent.length);
      
      // 创建CSV文件的Blob
      const blob = new Blob([csvContent], { 
        type: 'text/csv;charset=utf-8'
      });
      
      console.log('✅ Blob创建成功，大小:', blob.size, '字节');
      
      // 下载文件
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ CSV文件下载完成');
      
    } catch (error: any) {
      console.error('❌ 下载过程中出错:', error);
      console.error('❌ 错误详情:', {
        message: error?.message || '未知错误',
        stack: error?.stack || '无堆栈信息',
        filters: state.filters,
        params: params
      });
      throw error;
    }
  };

  // 生成CSV文件内容的函数
  const generateCSVContent = (params: any, productsData: Product[]) => {
    const { selected_fields, filters } = params;
    
    console.log('🔍 开始生成CSV内容，参数:', { selected_fields, filters });
    console.log('🔍 产品数据:', productsData);
    
    // 生成表头
    const headers = selected_fields.map((fieldKey: string) => {
      const field = DOWNLOAD_FIELDS.flatMap(group => group.fields).find(f => f.key === fieldKey);
      return field ? field.label : fieldKey;
    });
    
    console.log('🔍 生成的表头:', headers);
    
    // 使用传入的产品数据
    if (!productsData || productsData.length === 0) {
      console.warn('⚠️ 没有产品数据可下载');
      return '暂无数据';
    }
    
    // 构建CSV内容
    let csvContent = '\ufeff'; // 添加BOM，确保Excel正确识别中文
    csvContent += headers.join(',') + '\n';
    
    // 添加真实数据行
    productsData.forEach((product: Product, index: number) => {
      console.log(`🔍 处理第${index + 1}个产品:`, product);
      
      // 特别关注projects字段的处理
      if (selected_fields.includes('projects') && product.projects) {
        console.log(`🔍 产品${index + 1}的projects字段:`, {
          projects: product.projects,
          projectsType: typeof product.projects,
          isArray: Array.isArray(product.projects),
          length: product.projects.length,
          firstElement: product.projects[0],
          firstElementType: typeof product.projects[0]
        });
      }
      
      // 特别关注created_by字段的处理
      if (selected_fields.includes('created_by') && product.created_by) {
        console.log(`🔍 产品${index + 1}的created_by字段:`, {
          created_by: product.created_by,
          created_byType: typeof product.created_by,
          isObject: typeof product.created_by === 'object',
          hasUsername: product.created_by && typeof product.created_by === 'object' && (product.created_by as any).username,
          hasName: product.created_by && typeof product.created_by === 'object' && (product.created_by as any).name,
          hasId: product.created_by && typeof product.created_by === 'object' && (product.created_by as any).id
        });
      }
      
      const row = selected_fields.map(fieldKey => {
        try {
          const value = getProductFieldValue(product, fieldKey);
          console.log(`🔍 字段 ${fieldKey}: ${value}`);
          
          // 特别记录projects字段的处理结果
          if (fieldKey === 'projects') {
            console.log(`🔍 projects字段处理结果:`, {
              original: product.projects,
              processed: value,
              fieldKey
            });
          }
          
          return value;
        } catch (error) {
          console.error(`❌ 处理字段 ${fieldKey} 时出错:`, error, product);
          return `[错误: ${fieldKey}]`;
        }
      });
      
      // 处理包含逗号的字段，用双引号包围
      const processedRow = row.map(cell => {
        if (cell && cell.includes(',')) {
          return `"${cell}"`;
        }
        return cell || '';
      });
      csvContent += processedRow.join(',') + '\n';
    });
    
    console.log('📊 生成的CSV内容，包含真实数据:', productsData.length, '条记录');
    console.log('📊 CSV内容预览:', csvContent.substring(0, 500) + '...');
    
    // 返回CSV内容（Excel可以正常打开CSV文件）
    return csvContent;
  };

  // 获取产品字段值的辅助函数
  const getProductFieldValue = (product: Product, fieldKey: string): string => {
    switch (fieldKey) {
      case 'name':
        return product.name || '';
      case 'custom_code':
        return product.custom_code || '';
      case 'system_code':
        return product.system_code || '';
      case 'description':
        return product.description || '';
      case 'sorting_codes':
        if (product.sorting_codes && Array.isArray(product.sorting_codes)) {
          return product.sorting_codes.map(code => code.code).join(', ');
        }
        return '';
      case 'creation_source':
        if (product.creation_source) {
          const sourceMap: { [key: string]: string } = {
            'customer': '客户或仓库代创建',
            'warehouse': '仓库创建',
            'other': '其他'
          };
          return sourceMap[product.creation_source] || product.creation_source;
        }
        return '';
      case 'projects':
        if (product.projects && Array.isArray(product.projects)) {
          // 检查projects数组的第一个元素类型来判断数据结构
          if (product.projects.length > 0) {
            const firstProject = product.projects[0];
            
            if (typeof firstProject === 'number') {
              // 如果projects是number[]，返回ID
              return product.projects.join(', ');
            } else if (typeof firstProject === 'object' && firstProject !== null) {
              // 如果projects是对象数组，尝试提取项目信息
              try {
                return product.projects.map((project: any) => {
                  if (project && typeof project === 'object') {
                    // 尝试获取项目代码和名称
                    if (project.project_code && project.name) {
                      return `${project.project_code}-${project.name}`;
                    } else if (project.code && project.name) {
                      return `${project.code}-${project.name}`;
                    } else if (project.id) {
                      return `项目${project.id}`;
                    } else {
                      return '未知项目';
                    }
                  }
                  return String(project);
                }).join(', ');
              } catch (error) {
                console.warn('处理projects字段时出错:', error, product.projects);
                return '项目信息解析错误';
              }
            }
          }
          // 空数组情况
          return '';
        }
        return '';
      case 'customer':
        // 安全处理customer字段，可能为undefined
        if (product.customer !== undefined && product.customer !== null) {
          // 如果customer是对象，提取username
          if (typeof product.customer === 'object' && product.customer.username) {
            return product.customer.username;
          } else {
            return String(product.customer);
          }
        }
        return '';
      case 'value_usd':
        if (product.value_usd !== undefined && product.value_usd !== null) {
          return Number(product.value_usd).toFixed(2);
        }
        return '';
      case 'weight_grams':
        if (product.weight_grams !== undefined && product.weight_grams !== null) {
          return product.weight_grams.toString();
        }
        return '';
      case 'length_cm':
        if (product.length_cm !== undefined && product.length_cm !== null) {
          return product.length_cm.toString();
        }
        return '';
      case 'width_cm':
        if (product.width_cm !== undefined && product.width_cm !== null) {
          return product.width_cm.toString();
        }
        return '';
      case 'height_cm':
        if (product.height_cm !== undefined && product.height_cm !== null) {
          return product.height_cm.toString();
        }
        return '';
      case 'unit_volume':
        if (product.length_cm && product.width_cm && product.height_cm) {
          const volume = Number(product.length_cm) * Number(product.width_cm) * Number(product.height_cm);
          return volume.toFixed(2);
        }
        return '';
      case 'has_logistics_package':
        if (product.has_logistics_package !== undefined && product.has_logistics_package !== null) {
          return product.has_logistics_package ? '自带物流包装' : '无物流包装';
        }
        return '';
      case 'tracking_number':
        // 由于Product类型中没有tracking_number字段，返回空字符串
        return '';
      case 'created_at':
        console.log('🔥🔥🔥 进入created_at字段处理逻辑 🔥🔥🔥');
        if (product.created_at) {
          try {
            // 🔥 强制使用"年/月/日"格式，不使用toLocaleDateString
            const date = new Date(product.created_at);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDate = `${year}/${month}/${day}`;
            
            // 调试日志
            console.log('🔥 创建时间强制格式化:', {
              original: product.created_at,
              date: date,
              year: year,
              month: month,
              day: day,
              formatted: formattedDate,
              timestamp: Date.now(),
              productId: product.id,
              productName: product.name
            });
            
            console.log('🔥🔥🔥 返回格式化后的日期:', formattedDate, '🔥🔥🔥');
            // 使用零宽空格前缀强制Excel将字段识别为纯文本，避免自动转换为日期格式
            // 零宽空格(\u200B)在视觉上完全不可见，但Excel会将其识别为文本格式
            // 这样既不会有引号，又能确保Excel将其识别为文本，且完全不可见
            return `\u200B${formattedDate}`;
          } catch (error) {
            console.warn('❌ 创建时间格式化失败:', product.created_at, error);
            return product.created_at || '';
          }
        } else {
          console.log('🔥🔥🔥 created_at字段为空 🔥🔥🔥');
        }
        return '';
      case 'updated_at':
        if (product.updated_at) {
          try {
            // 格式化日期
            const date = new Date(product.updated_at);
            return date.toLocaleDateString('zh-CN');
          } catch (error) {
            console.warn('日期格式化失败:', product.updated_at, error);
            return product.updated_at || '';
          }
        }
        return '';
      case 'created_by':
        if (product.created_by !== undefined && product.created_by !== null) {
          // 检查created_by的类型
          if (typeof product.created_by === 'number') {
            // 如果created_by是number，返回ID
            return product.created_by.toString();
          } else if (typeof product.created_by === 'object' && product.created_by !== null) {
            // 如果created_by是对象，尝试提取用户信息
            try {
              const userObj = product.created_by as any;
              if (userObj.username) {
                return userObj.username;
              } else if (userObj.name) {
                return userObj.name;
              } else if (userObj.id) {
                return `用户${userObj.id}`;
              } else {
                return '未知用户';
              }
            } catch (error) {
              console.warn('处理created_by字段时出错:', error, product.created_by);
              return '用户信息解析错误';
            }
          } else {
            // 其他情况，转换为字符串
            return String(product.created_by);
          }
        }
        return '';
      default:
        return '';
    }
  };

  // 10. 获取筛选框当前值（处理类型转换）
  const getFilterValue = (key: string, value: any) => {
    switch (key) {
      case 'name':
        return value || '';
      case 'custom_code':
        return value || '';
      case 'system_code':
        return value || '';
      case 'sorting_codes':
        return value || '';
      case 'creation_source':
        return value || undefined;
      case 'projects':
        return value || undefined;
      case 'customer':
        // 统一以字符串ID作为Select值，确保与Option value类型一致
        if (value !== undefined && value !== null && value !== '') {
          return String(value);
        }
        return undefined;
      case 'has_logistics_package':
        // 支持四种状态：true(自带物流包装)、false(无物流包装)、"unlimited"(不限)、undefined(全部)
        if (value === "unlimited") {
          return undefined; // "不限"等同于取消筛选
        }
        return value === undefined ? undefined : value;
      default:
        return value;
    }
  };

  // 11. 表格列定义
  const columns: TableProps<Product>['columns'] = [
    // 1) 系统编码
    {
      title: '系统编码',
      dataIndex: 'system_code',
      key: 'system_code',
      width: 180,
    },
    // 2) 产品名称
    {
      title: '产品名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    // 3) 客户自定义编码
    {
      title: '客户自定义编码',
      dataIndex: 'custom_code',
      key: 'custom_code',
      width: 150,
    },
    // 4) 分拣条码（最多展示 3 条；超出用 ⋯ 打开弹窗；Hover 显示本产品条码说明）
    {
      title: '分拣条码',
      dataIndex: 'sorting_codes',
      key: 'sorting_codes',
      width: 220,
      render: (_: unknown, record: Product) => (
        <SortingCodesTableCell sorting_codes={record.sorting_codes} />
      ),
    },
    // 5) 已关联项目（最多 3 条；超出 ⋯ 弹窗查看全部；Hover 显示编码与名称）
    {
      title: '已关联项目',
      dataIndex: 'projects',
      key: 'projects',
      width: 220,
      render: (_: unknown, record: Product) => (
        <ProjectsTableCell projects={record.projects as unknown as ProjectListRow[] | undefined} />
      ),
    },
    // 6) 物流包装
    {
      title: '物流包装',
      dataIndex: 'has_logistics_package',
      key: 'has_logistics_package',
      width: 90,
      render: (value: boolean) => renderLogisticsPackageBadge(value),
    },
    // 7) 图片
    {
      title: '图片',
      key: 'photo',
      width: 90,
      render: (_: any, record: Product) => {
        const anyRec: any = record as any;
        const url = anyRec.photo || anyRec.photo_url || anyRec.image_url || anyRec.image;
        return url ? (
          <Image
            src={url}
            alt={record.name}
            width={56}
            height={56}
            style={{ objectFit: 'cover', borderRadius: 6 }}
            preview={{ src: url }}
            placeholder={true}
          />
        ) : <span style={{ color: '#999' }}>-</span>;
      }
    },
    // 8) 创建来源
    {
      title: '创建来源',
      dataIndex: 'creation_source',
      key: 'creation_source',
      width: 120,
      render: (value: string) => {
        const sourceMap: { [key: string]: string } = {
          'customer': '客户或仓库代创建',
          'warehouse': '仓库操作创建',
          'other': '其他'
        };
        return sourceMap[value] || value || '-';
      },
    },
    // 9) 操作列：查看详情（弹窗展示本产品全部序列化字段）、编辑、删除
    {
      title: '操作',
      key: 'more',
      width: 240,
      render: (_: unknown, record: Product) => (
        <Space size={0}>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setProductFullInfoRecord(record);
            }}
          >
            查看详情
          </Button>
          <Tooltip title={record.edit_limited ? record.edit_lock_reason : ''}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              disabled={record.can_edit === false}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${productBasePath}/products/${record.id}/edit`);
              }}
            >
              编辑
            </Button>
          </Tooltip>
          <Tooltip title={record.can_delete === false ? record.delete_lock_reason : ''}>
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={record.can_delete === false}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteProduct(record);
              }}
            >
              删除
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="product-list">
      <Card>
        {/* 自定义样式 */}
        <style>{`
          .compact-multiselect .ant-select-selection-item {
            margin: 2px 4px 2px 0 !important;
            padding: 2px 8px !important;
            font-size: 12px !important;
            line-height: 16px !important;
            max-width: 280px !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }
          .compact-multiselect .ant-select-selection-overflow {
            max-height: 32px !important;
            overflow: hidden !important;
          }
          .compact-multiselect .ant-select-selection-overflow-item {
            margin: 2px 4px 2px 0 !important;
          }
          .compact-multiselect .ant-select-selection-overflow-item-rest {
            margin: 2px 4px 2px 0 !important;
            padding: 2px 8px !important;
            background-color: #f0f0f0 !important;
            border-radius: 4px !important;
            font-size: 12px !important;
            color: #666 !important;
          }
          .product-full-info-modal .ant-modal-header .ant-modal-title {
            margin-right: 52px;
          }
          .product-full-info-modal .product-copy-info-btn.ant-btn {
            background-color: #ecfdf5 !important;
            border-color: #a7f3d0 !important;
            color: #047857 !important;
          }
          .product-full-info-modal .product-copy-info-btn.ant-btn:not(:disabled):hover {
            background-color: #d1fae5 !important;
            border-color: #6ee7b7 !important;
            color: #065f46 !important;
          }
          .product-full-info-modal .product-copy-info-btn.ant-btn:disabled {
            opacity: 0.55;
            background-color: #f0fdf4 !important;
            border-color: #d9f99d !important;
            color: #86efac !important;
          }
        `}</style>

        <Modal
          wrapClassName="product-full-info-modal"
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                paddingRight: 8,
                boxSizing: 'border-box',
              }}
            >
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {productFullInfoRecord
                  ? `产品详情 — ${productFullInfoRecord.name}`
                  : '产品详情'}
              </span>
              <Button
                type="default"
                size="small"
                className="product-copy-info-btn"
                icon={<CopyOutlined />}
                style={{ flexShrink: 0 }}
                disabled={!productFullInfoRecord}
                onClick={async () => {
                  if (!productFullInfoRecord) return;
                  const text = buildProductFullInfoPlainText(
                    productFullInfoRecord,
                    hideCustomerByStrategy,
                  );
                  const ok = await copyTextToClipboard(text);
                  if (ok) {
                    message.success('已复制到剪贴板');
                  } else {
                    message.error('复制失败，请手动复制');
                  }
                }}
              >
                复制信息
              </Button>
            </div>
          }
          open={!!productFullInfoRecord}
          onCancel={() => setProductFullInfoRecord(null)}
          width={760}
          footer={
            <Button type="primary" onClick={() => setProductFullInfoRecord(null)}>
              关闭
            </Button>
          }
          destroyOnClose
          styles={{ body: { paddingTop: 12 } }}
        >
          {productFullInfoRecord ? (
            <ProductFullInfoModalBody
              product={productFullInfoRecord}
              hideCustomer={hideCustomerByStrategy}
            />
          ) : null}
        </Modal>
        
        {/* 1. 页面标题和操作区域 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Title level={4} style={{ margin: 0 }}>产品列表</Title>
              <div className="flex items-center space-x-2">
                <Tag color="blue" style={{ fontSize: '14px', padding: '8px' }}>
                  共 {state.pagination.total} 条记录
                </Tag>
              </div>
            </div>
            
            {/* 右侧操作按钮（与入库预报 List 一致：创建类按钮组 + 竖线 + 批量下载） */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2" style={{ marginRight: 16 }}>
                <Tooltip title={canCreateProduct ? '' : '无权限'}>
                  <span>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      size="small"
                      onClick={() => {
                        const basePath =
                          user?.role === 'admin'
                            ? '/dashboard/admin'
                            : user?.role === 'staff'
                              ? '/dashboard/staff'
                              : '/dashboard/customer';
                        navigate(`${basePath}/products/create`);
                      }}
                      disabled={!canCreateProduct}
                    >
                      创建产品
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={canCreateProduct ? '' : '无权限'}>
                  <span>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      size="small"
                      disabled={!canCreateProduct}
                        onClick={() => {
                          const basePath =
                            user?.role === 'admin'
                              ? '/dashboard/admin'
                              : user?.role === 'staff'
                                ? '/dashboard/staff'
                                : '/dashboard/customer';
                          navigate(`${basePath}/products/batch-create`);
                        }}
                      >
                        批量创建产品
                    </Button>
                  </span>
                </Tooltip>
              </div>
              <Divider
                type="vertical"
                style={{ height: 24, margin: '0 8px', borderWidth: 2, borderColor: '#d9d9d9' }}
              />
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                size="small"
                loading={downloading}
                onClick={handleShowDownloadModal}
                disabled={state.pagination.total === 0}
              >
                批量下载 ({state.pagination.total} 条)
              </Button>
            </div>
          </div>
        </div>

        {/* 错误信息显示 */}
        {error && (
          <div style={{ marginBottom: 16 }}>
            <Card size="small" style={{ borderColor: '#ff4d4f' }}>
              <div className="text-red-600">
                <strong>❌ 获取数据失败:</strong> {error}
              </div>
              <div className="mt-2 text-sm text-gray-600">
                请检查网络连接和认证状态，或联系管理员
              </div>
            </Card>
          </div>
        )}

        {/* 2. 筛选组件 */}
        <div style={{ marginBottom: 16 }}>
          <Form layout="horizontal">
            {/* 第一行：主要筛选条件 */}
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="搜索">
                  <Input 
                    placeholder="模糊搜索产品名称/客户自定义编码/产品描述"
                    prefix={<SearchOutlined />}
                    allowClear
                    value={getFilterValue('name', state.filters?.name)}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="客户自定义编码">
                  <Input
                    placeholder="输入客户自定义编码"
                    allowClear
                    value={getFilterValue('custom_code', state.filters?.custom_code)}
                    onChange={(e) => handleFilterChange('custom_code', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="系统编码">
                  <Input
                    placeholder="输入系统编码"
                    allowClear
                    value={getFilterValue('system_code', state.filters?.system_code)}
                    onChange={(e) => handleFilterChange('system_code', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="分拣条码">
                  <Input
                    placeholder="输入分拣条码"
                    allowClear
                    value={getFilterValue('sorting_codes', state.filters?.sorting_codes)}
                    onChange={(e) => handleFilterChange('sorting_codes', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            {/* 第二行：创建来源、所属项目、物流包装和操作按钮 */}
            <Row gutter={16}>
              {/* 客户筛选框 - 仅对员工和管理员显示，且未被敏感策略隐藏 */}
              {user && (user.role === 'admin' || user.role === 'staff') && !hideCustomerByStrategy && (
                <Col span={4}>
                  <Form.Item label="所属客户">
                    <Select
                      placeholder="请选择客户"
                      allowClear
                      loading={customersLoading}
                      showSearch
                      optionFilterProp="children"
                      value={getFilterValue('customer', state.filters?.customer)}
                      onChange={(value) => handleFilterChange('customer', value)}
                      style={{ width: '100%' }}
                      dropdownStyle={{ maxHeight: 300, minWidth: '320px' }}
                      notFoundContent={
                        <div>
                          <div>暂无客户数据</div>
                          <Button 
                            type="link" 
                            size="small" 
                            onClick={() => {
                              // 重新获取客户列表
                              const fetchCustomers = async () => {
                                setCustomersLoading(true);
                                try {
                                  const customersData = await getCustomerList();
                                  setCustomers(customersData);
                                } catch (error) {
                                  console.error('重新获取客户列表失败:', error);
                                } finally {
                                  setCustomersLoading(false);
                                }
                              };
                              fetchCustomers();
                            }}
                            loading={customersLoading}
                          >
                            重新加载
                          </Button>
                        </div>
                      }
                    >
                      {customers.map(customer => (
                        <Option key={customer.id} value={customer.id}>
                          {customer.username}
                        </Option>
                      ))}
                    </Select>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>客户数：{customers.length}</div>
                  </Form.Item>
                </Col>
              )}
              <Col span={4}>
                <Form.Item label="创建来源">
                  <Select
                    placeholder="选择创建来源"
                    allowClear
                    value={getFilterValue('creation_source', state.filters?.creation_source)}
                    onChange={(value) => handleFilterChange('creation_source', value)}
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 200 }}
                  >
                    <Option value="customer">客户或仓库代创建</Option>
                    <Option value="warehouse">仓库操作创建</Option>
                    <Option value="other">其他</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="所属项目">
                  <Select
                    placeholder="请选择所属项目 可以多选"
                    allowClear
                    mode="multiple"
                    showSearch
                    filterOption={(input, option) => {
                      if (!option?.label) return false;
                      const searchText = input.toLowerCase();
                      const optionText = option.label.toString().toLowerCase();
                      return optionText.includes(searchText);
                    }}
                    value={state.filters?.projects ? 
                           state.filters.projects.split(',').filter(Boolean) : undefined}
                    onChange={(value) => handleFilterChange('projects', value ? value.join(',') : undefined)}
                    loading={projectsLoading}
                    notFoundContent={projectsLoading ? '加载中...' : projects.length === 0 ? '暂无项目' : '未找到匹配的项目'}
                    maxTagCount={1}
                    maxTagTextLength={80}
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 300, minWidth: '320px' }}
                    optionLabelProp="label"
                    className="compact-multiselect"
                  >
                    {projects.map(project => (
                      <Option 
                        key={project.id} 
                        value={project.id.toString()}
                        label={`${project.project_code} - ${project.name}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{project.project_code}</span>
                          <span className="text-gray-500 ml-2">{project.name}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                  {process.env.NODE_ENV === 'development' && (
                    <div className="text-xs text-gray-500 mt-1">
                      已加载 {projects.length} 个项目
                    </div>
                  )}
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="物流包装">
                    <Select
                    placeholder="选择物流包装状态"
                      allowClear
                    value={getFilterValue('has_logistics_package', state.filters?.has_logistics_package)}
                    onChange={(value) => handleFilterChange('has_logistics_package', value)}
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 200 }}
                  >
                    <Option value={true}>自带物流包装</Option>
                    <Option value={false}>无物流包装</Option>
                    <Option value="unlimited">不限</Option>
                    </Select>
                  </Form.Item>
                </Col>
              <Col span={4}>
                <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      loading={loading}
                      onClick={() => {
                        // 触发数据获取
                        actions.refresh();
                      }}
                    >
                      搜索
                    </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleReset}
                  >
                    重置
                  </Button>
                  </Space>
                </Form.Item>
                </Col>
              </Row>
          </Form>
        </div>

        {/* 3. 数据表格 */}
        <div style={{ marginBottom: 16 }}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={false}
            onChange={handleTableChange}
            expandable={{
              expandedRowKeys: expandedRows,
              onExpand: (expanded, record) => {
                if (expanded) {
                  setExpandedRows([...expandedRows, record.id.toString()]);
                } else {
                  setExpandedRows(expandedRows.filter(id => id !== record.id.toString()));
                }
              }
            }}
          />
        </div>

        {/* 4. 分页组件 */}
        <div className="flex justify-end items-center">
          <Pagination
            current={state.pagination.current}
            pageSize={state.pagination.pageSize}
            total={state.pagination.total}
            showSizeChanger={true}
            showQuickJumper={true}
            showTotal={(total: number, range: [number, number]) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
            }
            pageSizeOptions={['10', '20', '50', '100']}
            size="default"
            className="mt-4"
            onChange={(page, pageSize) => {
              actions.updatePagination({ current: page, pageSize: pageSize || 20 });
            }}
            onShowSizeChange={(current, size) => {
              actions.updatePagination({ current: 1, pageSize: size });
            }}
          />
        </div>

        {/* 批量下载字段选择弹窗 - 基于用户角色显示字段 */}
        <Modal
          title="选择下载字段"
          open={downloadModalVisible}
          onCancel={() => setDownloadModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setDownloadModalVisible(false)}>
              取消
            </Button>,
            <Button
              key="confirm"
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={() => handleConfirmDownload(selectedDownloadFields)}
              disabled={selectedDownloadFields.length === 0}
            >
              确认下载 ({selectedDownloadFields.length} 个字段)
            </Button>
          ]}
          width={600}
          destroyOnClose
        >
          <div className="space-y-4">
            {/* 全选控制 */}
            <div className="border-b pb-3">
              <Checkbox
                checked={selectedDownloadFields.length === getAllFieldsCount()}
                indeterminate={selectedDownloadFields.length > 0 && selectedDownloadFields.length < getAllFieldsCount()}
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                <span className="font-medium">全选/取消全选</span>
              </Checkbox>
            </div>

            {/* 字段分组 - 基于用户角色显示 */}
            {(() => {
              const currentFields = getFieldsByRole(user?.role || 'customer');
              return currentFields.map((group, groupIndex) => {
                const groupFields = group.fields.map(field => field.key);
                const groupSelectedCount = groupFields.filter(field => selectedDownloadFields.includes(field)).length;
                const isGroupSelected = checkGroupSelected(group);
                const isGroupIndeterminate = checkGroupIndeterminate(group);

                return (
                  <div key={groupIndex} className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={isGroupSelected}
                        indeterminate={isGroupIndeterminate}
                        onChange={(e) => handleGroupSelect(groupFields, e.target.checked)}
                      >
                        <span className="font-medium text-blue-600">
                          {group.group} ({groupSelectedCount}/{groupFields.length})
                        </span>
                      </Checkbox>
                    </div>
                    
                    <div className="ml-6 grid grid-cols-2 gap-2">
                      {group.fields.map((field) => (
                        <Checkbox
                          key={field.key}
                          checked={selectedDownloadFields.includes(field.key)}
                          onChange={(e) => handleFieldChange(field.key, e.target.checked)}
                        >
                          {field.label}
                        </Checkbox>
                      ))}
                    </div>
                    
                    {groupIndex < currentFields.length - 1 && <Divider />}
                  </div>
                );
              });
            })()}

            {/* 提示信息 */}
            <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
              💡 提示：选择的字段越多，文件越大，下载时间越长。建议只选择需要的字段。文件格式为CSV，Excel可以正常打开。
            </div>
          </div>
        </Modal>
      </Card>
    </div>
  );
};

export default ProductList; 